import { makeAutoObservable } from 'mobx'

// Minimal nav state. Just two things:
//   1. Is the rail pinned open? (vs collapsed-to-icons with hover-to-expand)
//   2. Which collapsible sections are expanded?
//
// Everything else — active item, sync info, user — comes from the URL or
// other stores. We intentionally do NOT mirror "current page" here; it just
// drifts out of sync with the router.

const PIN_KEY = 'navV2.pinned'
const WIDTH_KEY = 'navV2.widthPx'
const DEFAULT_WIDTH = 256
export const NAV_V2_MIN_WIDTH = 220
export const NAV_V2_MAX_WIDTH = 420
// We persist which sections the user has explicitly *collapsed*. Default
// state (empty set) = everything open. This way new sections are open by
// default without having to seed them anywhere.
const COLLAPSED_KEY = 'navV2.collapsed'

const readBool = (key: string, fallback: boolean): boolean => {
    try {
        const v = localStorage.getItem(key)
        if (v === null) return fallback
        return v === '1'
    } catch {
        return fallback
    }
}

const readArray = (key: string, fallback: string[]): string[] => {
    try {
        const v = localStorage.getItem(key)
        if (!v) return fallback
        const parsed = JSON.parse(v)
        return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string') : fallback
    } catch {
        return fallback
    }
}

const readNumber = (key: string, fallback: number): number => {
    try {
        const raw = localStorage.getItem(key)
        if (!raw) return fallback
        const parsed = Number.parseInt(raw, 10)
        return Number.isFinite(parsed) ? parsed : fallback
    } catch {
        return fallback
    }
}

const clampWidth = (width: number): number => {
    return Math.min(NAV_V2_MAX_WIDTH, Math.max(NAV_V2_MIN_WIDTH, Math.round(width)))
}

class NavV2Store {
    // Default: pinned/expanded on first load (per user spec).
    pinned = readBool(PIN_KEY, true)
    widthPx = clampWidth(readNumber(WIDTH_KEY, DEFAULT_WIDTH))
    // Sections the user has explicitly collapsed. Everything else is open.
    collapsed = new Set<string>(readArray(COLLAPSED_KEY, []))

    constructor() {
        makeAutoObservable(this)
    }

    setPinned(v: boolean) {
        this.pinned = v
        try {
            localStorage.setItem(PIN_KEY, v ? '1' : '0')
        } catch {}
    }

    togglePinned() {
        this.setPinned(!this.pinned)
    }

    setWidth(width: number) {
        this.widthPx = clampWidth(width)
        try {
            localStorage.setItem(WIDTH_KEY, String(this.widthPx))
        } catch {}
    }

    get widthStyle() {
        return `${this.widthPx}px`
    }

    isCollapsed(key: string): boolean {
        return this.collapsed.has(key)
    }

    toggleSection(key: string) {
        if (this.collapsed.has(key)) {
            this.collapsed.delete(key)
        } else {
            this.collapsed.add(key)
        }
        try {
            localStorage.setItem(COLLAPSED_KEY, JSON.stringify(Array.from(this.collapsed)))
        } catch {}
    }
}

export const navV2Store = new NavV2Store()
